/**
 * Keyboard-axis → transform-space resolution.
 *
 * When the user presses X/Y/Z during an active command, the raw axis letter
 * must be mapped through the current transform orientation (global / local /
 * view / normal) so it matches what the gizmo would produce if the user had
 * clicked the same axis handle. This lives in its own module because it's
 * used by both enterCommand (resolving a pending axis stashed before the
 * command started) and setCommandAxis (live axis toggles during a command).
 *
 * Keeping these helpers pure and stateless — taking `state` explicitly rather
 * than closing over `get` — lets them be imported without pulling in the
 * whole factory's closure.
 */

import * as THREE from "three/webgpu";
import { computeSelectionNormal } from "../../modeling/meshOps.ts";
import {
  computeOrientationQuat,
  mapHandleToConstraint,
} from "../../viewport/gizmo/gizmoOrientation.ts";
import { composeSceneObjectWorldQuaternion } from "../collectionGraph.ts";
import type { AppState, AxisConstraint } from "../storeTypes.ts";
import { _kbQuat, _kbVec } from "./core.ts";

/**
 * Convert a keyboard axis constraint ("x"/"y"/"z") into the correct
 * constraint for the current transform orientation, matching
 * mapHandleToConstraint's gizmo logic.
 *
 * In edit mode with non-local orientations, the axis direction must be
 * expressed in object-local space because vertex transforms operate there.
 */
export function resolveKeyboardAxis(
  state: AppState,
  axis: AxisConstraint,
): { axis: AxisConstraint; normalVector?: [number, number, number] } {
  if (!axis || axis === "normal") return { axis };

  const orientation = state.transformOrientation;

  // Global orientation: raw axis maps directly to world axes in any mode
  if (orientation === "global") {
    const inEditMode = state.mode === "edit" && state.editMode != null;
    if (!inEditMode) return { axis };
    // In edit mode, global still needs to go through mapHandleToConstraint
    // to convert world direction into object-local space. Use the *world*
    // rotation (collection.rotation ∘ obj local) so the axis mapping matches
    // what the gizmo would produce — see gizmoOrientation.mapHandleToConstraint.
    const em = state.editMode!;
    const objectQuat = composeSceneObjectWorldQuaternion(
      state.sceneObjects,
      state.collections,
      em.objectId,
      _kbQuat,
    );
    if (!objectQuat) return { axis };
    const orientationQuat = new THREE.Quaternion().identity();
    return mapHandleToConstraint(
      axis,
      orientation,
      orientationQuat,
      objectQuat,
      true,
    );
  }

  // Local orientation: raw axis is already the object's local axis.
  // In edit mode, vertex transforms are in object-local space so the raw
  // axis is correct. In object mode, the axis maps to a world-space direction
  // that depends on the object's rotation.
  const inEditMode = state.mode === "edit" && state.editMode != null;
  if (orientation === "local") {
    if (inEditMode) return { axis };
    // Object mode local: rotate the cardinal axis by the object's rotation
    // to get the world-space direction.
    const objectQuat = getFirstSelectedObjectQuat(state);
    if (!objectQuat) return { axis };
    const orientationQuat = objectQuat.clone();
    return mapHandleToConstraint(
      axis,
      orientation,
      orientationQuat,
      objectQuat,
      false,
    );
  }

  // View / Normal orientations — need the camera
  const camera = window.__poseref_camera;

  if (inEditMode) {
    const em = state.editMode!;
    const obj = state.sceneObjects.get(em.objectId);
    if (!obj) return { axis };
    // World rotation — matches the gizmo's objectQuat and keeps the axis
    // mapping consistent for collection members.
    const objectQuat = composeSceneObjectWorldQuaternion(
      state.sceneObjects,
      state.collections,
      em.objectId,
      _kbQuat,
    );
    if (!objectQuat) return { axis };

    // Compute selection normal for Normal orientation (matches gizmo path)
    let normalDir: THREE.Vector3 | null = null;
    if (
      orientation === "normal" &&
      !em.isCurve &&
      obj.loopNormals &&
      obj.topology
    ) {
      const n = computeSelectionNormal(
        obj.vertices,
        obj.loopNormals,
        obj.topology,
        em.selectedVertices,
      );
      const len = Math.sqrt(n[0] * n[0] + n[1] * n[1] + n[2] * n[2]);
      if (len > 1e-10) {
        normalDir = _kbVec.set(n[0], n[1], n[2]);
      }
    }

    const orientationQuat = !camera
      ? _kbQuat.clone().identity()
      : computeOrientationQuat(orientation, objectQuat, normalDir, camera);
    return mapHandleToConstraint(
      axis,
      orientation,
      orientationQuat,
      objectQuat,
      true,
    );
  }

  // Object mode with view/normal orientation
  const objectQuat = getFirstSelectedObjectQuat(state);
  if (!objectQuat || !camera) return { axis };
  const orientationQuat = computeOrientationQuat(
    orientation,
    objectQuat,
    null,
    camera,
  );
  return mapHandleToConstraint(
    axis,
    orientation,
    orientationQuat,
    objectQuat,
    false,
  );
}

/** Get world-rotation quaternion of the first selected object (for keyboard axis resolution). */
export function getFirstSelectedObjectQuat(
  state: AppState,
): THREE.Quaternion | null {
  for (const target of state.selectedObjectTargets) {
    if (target.kind === "sceneObject") {
      // World rotation via the scene-graph helper so a collection-member
      // scene object returns `collection.rotation ∘ obj local`, not the
      // child-local rotation alone.
      const q = composeSceneObjectWorldQuaternion(
        state.sceneObjects,
        state.collections,
        target.id,
        _kbQuat,
      );
      if (q) return q.clone();
    } else if (target.kind === "blendEntry") {
      const gt = state.blendFiles.get(target.id)?.groupTransform;
      if (gt) {
        return new THREE.Quaternion(
          gt.rotation[0],
          gt.rotation[1],
          gt.rotation[2],
          gt.rotation[3],
        );
      }
    } else if (target.kind === "collection") {
      const gt = state.collections.get(target.id)?.transform;
      if (gt) {
        return new THREE.Quaternion(
          gt.rotation[0],
          gt.rotation[1],
          gt.rotation[2],
          gt.rotation[3],
        );
      }
    }
  }
  return null;
}
